import PictureImage from '../Common/PictureImage';
import { useState, useRef, useMemo, type ReactNode } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { useInViewOnce } from "../../hooks/useInViewOnce";
import {
  FORMSPREE_CONTACT_FORM,
  buildFreeCallCustomerAutoresponse,
  readFormspreeErrorMessage,
  submitFormspreeNativePost,
} from "../../utils/formspree";
import { trackLeadSubmission } from "../../utils/trackLead";
import "./layout.css";
import { absoluteUrl } from "../../seo/siteOrigin";

const useScrollInView = () => {
  const ref = useRef<HTMLDivElement | null>(null);
  const isInView = useInViewOnce(ref, { once: true });
  return [ref, isInView] as const;
};

const benefits: { icon: string; title: string; desc: ReactNode }[] = [
  {
    icon: "✅",
    title: "No Setup Fees",
    desc: (
      <>
        No upfront costs — review our{" "}
        <Link to="/amazon-fba-prep-pricing" className="bfc-inline-link">transparent FBA prep pricing</Link> and pay only for the services you use.
      </>
    ),
  },
  {
    icon: "✅",
    title: "UK Based Team",
    desc: (
      <>
        Our UK-based prep specialists at our{" "}
        <Link to="/amazon-fba-prep-centre-services" className="bfc-inline-link">Amazon FBA prep centre</Link> are ready whenever you need support.
      </>
    ),
  },
  {
    icon: "✅",
    title: "250+ Clients Served",
    desc: (
      <>
        Trusted by Amazon sellers — from{" "}
        <Link to="/amazon-fba-prep-private-label" className="bfc-inline-link">private label</Link> to{" "}
        <Link to="/amazon-fba-prep-wholesale" className="bfc-inline-link">wholesale FBA prep</Link> across the UK, Europe, and beyond.
      </>
    ),
  },
];

/** Hero photo — consultation / workspace */
const HERO_BG_SRC =
  "/amazonfba/bookafreecall.webp";

/** Brand tokens — aligned with `amazonFBAprep.css` */
const COL = {
  blue: "#28479e",
  blueDark: "#1b2f6d",
  orange: "#f58030",
  text: "#1f2937",
  textMuted: "#4b5563",
  textSoft: "#6b7280",
  border: "#e5e7eb",
  surface: "#ffffff",
  surfaceSubtle: "#f9fafb",
} as const;

const UK_TIME_SLOTS = ["09:00", "09:30", "10:00", "10:30", "11:00", "11:30"] as const;

const WEEKDAY_LABELS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

function startOfLocalDay(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
}

function isBeforeTodayLocal(d: Date): boolean {
  return startOfLocalDay(d) < startOfLocalDay(new Date());
}

function sameLocalDay(a: Date, b: Date): boolean {
  return (
    a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate()
  );
}

function formatLongDate(d: Date): string {
  return d.toLocaleDateString("en-GB", { weekday: "long", day: "numeric", month: "long", year: "numeric" });
}

type BookingStep = "schedule" | "details";

type DetailForm = {
  name: string;
  email: string;
  phone: string;
  companyTradingName: string;
  yearsTrading: string;
  fulfilment: boolean;
  fbaPrep: boolean;
  logistics: boolean;
};

const consultationPoints = [
  {
    title: "Personalised strategy review",
    body: "We look at your current FBA prep, fulfilment flow, inventory pain points and growth plans — tailored to how you actually sell.",
  },
  {
    title: "Clear, practical recommendations",
    body: "Honest feedback on what to fix first, what can wait and where Pick Pack Pro can save you time and margin — with no hard sell.",
  },
  {
    title: "Structured next steps",
    body: "If we’re a good fit, we outline timelines, pricing bands and onboarding. If not, you still leave with a useful checklist.",
  },
  {
    title: "UK team, ~20 minutes",
    body: "A focused call with our Milton Keynes–based specialists, typically around twenty minutes, at a UK time that suits you.",
  },
] as const;
void consultationPoints;

const initialDetailForm: DetailForm = {
  name: "",
  email: "",
  phone: "",
  companyTradingName: "",
  yearsTrading: "",
  fulfilment: false,
  fbaPrep: false,
  logistics: false,
};

export default function BookAFreeCall() {
  const navigate = useNavigate();
  const [bookingStep, setBookingStep] = useState<BookingStep>("schedule");
  const [calendarCursor, setCalendarCursor] = useState(() => {
    const n = new Date();
    return new Date(n.getFullYear(), n.getMonth(), 1);
  });
  const [selectedDay, setSelectedDay] = useState<Date | null>(null);
  const [selectedSlot, setSelectedSlot] = useState<(typeof UK_TIME_SLOTS)[number] | null>(null);
  const [detailForm, setDetailForm] = useState<DetailForm>(initialDetailForm);
  const [focusedField, setFocusedField] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");

  const [heroRef, heroInView] = useScrollInView();
  const [formRef, formInView] = useScrollInView();
  const [benefitsRef, benefitsInView] = useScrollInView();

  const { year: viewY, month: viewM, daysInMonth, firstWeekdayMon0 } = useMemo(() => {
    const y = calendarCursor.getFullYear();
    const m = calendarCursor.getMonth();
    const dim = new Date(y, m + 1, 0).getDate();
    const firstDowSun0 = new Date(y, m, 1).getDay();
    const firstWeekdayMon0 = (firstDowSun0 + 6) % 7;
    return { year: y, month: m, daysInMonth: dim, firstWeekdayMon0 };
  }, [calendarCursor]);

  const monthLabel = calendarCursor.toLocaleDateString("en-GB", { month: "long", year: "numeric" });

  const calendarCells = useMemo(() => {
    const cells: Array<{ day: number; date: Date } | null> = [];
    for (let i = 0; i < firstWeekdayMon0; i++) cells.push(null);
    for (let day = 1; day <= daysInMonth; day++) {
      cells.push({ day, date: new Date(viewY, viewM, day) });
    }
    return cells;
  }, [firstWeekdayMon0, daysInMonth, viewY, viewM]);

  const goPrevMonth = () => {
    setCalendarCursor((c) => new Date(c.getFullYear(), c.getMonth() - 1, 1));
  };

  const goNextMonth = () => {
    setCalendarCursor((c) => new Date(c.getFullYear(), c.getMonth() + 1, 1));
  };

  const canProceedToDetails = Boolean(selectedDay && selectedSlot);

  const handleDetailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;
    if (type === "checkbox") {
      setDetailForm((prev) => ({ ...prev, [name]: checked }));
    } else {
      setDetailForm((prev) => ({ ...prev, [name]: value }));
    }
    if (submitError) setSubmitError("");
  };

  const selectedServicesText = () => {
    const parts: string[] = [];
    if (detailForm.fulfilment) parts.push("Fulfilment");
    if (detailForm.fbaPrep) parts.push("FBA Prep");
    if (detailForm.logistics) parts.push("Logistics");
    return parts.join(", ");
  };

  const validateDetails = (): string | null => {
    if (!detailForm.name.trim()) return "Please enter your name.";
    if (!detailForm.email.trim()) return "Please enter your email.";
    if (!detailForm.companyTradingName.trim()) return "Please enter your company trading name.";
    if (!detailForm.yearsTrading.trim()) return "Please enter how many years you have been trading.";
    if (!detailForm.fulfilment && !detailForm.fbaPrep && !detailForm.logistics) {
      return "Please select at least one service requirement.";
    }
    return null;
  };

  const handleBookingSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const v = validateDetails();
    if (v) {
      setSubmitError(v);
      return;
    }
    if (!selectedDay || !selectedSlot) {
      setSubmitError("Please go back and choose a date and time.");
      return;
    }

    setIsSubmitting(true);
    setSubmitError("");

    const preferredDate = formatLongDate(selectedDay);
    const preferredDateISO = `${selectedDay.getFullYear()}-${String(selectedDay.getMonth() + 1).padStart(2, "0")}-${String(selectedDay.getDate()).padStart(2, "0")}`;
    const services = selectedServicesText();
    const subject = `Free call booking — ${detailForm.companyTradingName.trim() || detailForm.name.trim()}`;
    const thankYouUrl = absoluteUrl('/thank-you');

    const controller = new AbortController();
    const timeoutMs = 60000;
    const timeoutId = window.setTimeout(() => controller.abort(), timeoutMs);

    try {
      const customerEmail = detailForm.email.trim();
      const customerName = detailForm.name.trim();
      const company = detailForm.companyTradingName.trim();
      const autoresponse = buildFreeCallCustomerAutoresponse({
        name: customerName,
        email: customerEmail,
        company,
        preferredDateLabel: preferredDate,
        preferredTimeUK: selectedSlot,
        services,
        phone: detailForm.phone.trim(),
      });

      const fd = new FormData();
      fd.append("name", customerName);
      fd.append("email", customerEmail);
      fd.append("phone", detailForm.phone.trim());
      fd.append("companyTradingName", company);
      fd.append("yearsTrading", detailForm.yearsTrading.trim());
      fd.append("serviceRequirements", services);
      fd.append("preferredCallDate", preferredDateISO);
      fd.append("preferredCallDateLabel", preferredDate);
      fd.append("preferredCallTimeUK", selectedSlot);
      fd.append("message", [
        `Preferred call: ${preferredDate} at ${selectedSlot} (UK / London)`,
        `Company: ${company}`,
        `Years trading: ${detailForm.yearsTrading.trim()}`,
        `Services: ${services}`,
        detailForm.phone.trim() ? `Phone: ${detailForm.phone.trim()}` : "",
      ]
        .filter(Boolean)
        .join("\n"));
      fd.append("_replyto", customerEmail);
      fd.append("_subject", subject);
      fd.append("_next", thankYouUrl);
      // Customer confirmation email (Formspree Autoresponse plugin must be ON)
      fd.append("_autoresponse", autoresponse);
      // Extra copy of the booking notification to the customer (where plan allows)
      fd.append("_cc", customerEmail);

      const response = await fetch(FORMSPREE_CONTACT_FORM, {
        method: "POST",
        body: fd,
        headers: { Accept: "application/json" },
        signal: controller.signal,
      });

      if (response.ok) {
        trackLeadSubmission({
          source: 'book-a-free-call',
          name: customerName,
          email: customerEmail,
          phone: detailForm.phone.trim(),
          company,
          message: [
            `Preferred call: ${preferredDate} at ${selectedSlot} (UK / London)`,
            `Years trading: ${detailForm.yearsTrading.trim()}`,
            `Services: ${services}`,
          ].join('\n'),
          extra: {
            preferredCallDate: preferredDateISO,
            preferredCallTimeUK: selectedSlot,
            yearsTrading: detailForm.yearsTrading.trim(),
            services,
          },
        });
        setIsSubmitting(false);
        window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
        navigate("/thank-you", {
          state: {
            callBooking: {
              email: customerEmail,
              preferredDateLabel: preferredDate,
              preferredTimeUK: selectedSlot,
            },
          },
        });
        return;
      } else {
        setSubmitError(await readFormspreeErrorMessage(response));
        setIsSubmitting(false);
      }
    } catch (error) {
      const aborted = error instanceof DOMException && error.name === "AbortError";
      if (aborted) {
        setSubmitError("Request timed out. Please try again or email info@pickpackpro.co.uk.");
        setIsSubmitting(false);
      } else {
        try {
          const customerEmail = detailForm.email.trim();
          const customerName = detailForm.name.trim();
          const company = detailForm.companyTradingName.trim();
          const autoresponse = buildFreeCallCustomerAutoresponse({
            name: customerName,
            email: customerEmail,
            company,
            preferredDateLabel: preferredDate,
            preferredTimeUK: selectedSlot,
            services,
            phone: detailForm.phone.trim(),
          });
          submitFormspreeNativePost(
            FORMSPREE_CONTACT_FORM,
            [
              ["name", customerName],
              ["email", customerEmail],
              ["phone", detailForm.phone.trim()],
              ["companyTradingName", company],
              ["yearsTrading", detailForm.yearsTrading.trim()],
              ["serviceRequirements", services],
              ["preferredCallDate", preferredDateISO],
              ["preferredCallDateLabel", preferredDate],
              ["preferredCallTimeUK", selectedSlot],
              [
                "message",
                [
                  `Preferred call: ${preferredDate} at ${selectedSlot} (UK / London)`,
                  `Company: ${company}`,
                  `Years trading: ${detailForm.yearsTrading.trim()}`,
                  `Services: ${services}`,
                  detailForm.phone.trim() ? `Phone: ${detailForm.phone.trim()}` : "",
                ]
                  .filter(Boolean)
                  .join("\n"),
              ],
              ["_replyto", customerEmail],
              ["_subject", subject],
              ["_autoresponse", autoresponse],
              ["_cc", customerEmail],
            ],
            thankYouUrl
          );
        } catch {
          setSubmitError(
            "We could not send your booking from this browser. Please email info@pickpackpro.co.uk or try another network."
          );
          setIsSubmitting(false);
        }
      }
    } finally {
      window.clearTimeout(timeoutId);
    }
  };

  const inputStyle = (field: string) => ({
    width: "100%" as const,
    padding: "14px 16px",
    background: focusedField === field ? "rgba(40, 71, 158, 0.06)" : COL.surface,
    border: `1.5px solid ${focusedField === field ? COL.blue : COL.border}`,
    borderRadius: "10px",
    color: COL.text,
    fontSize: "15px",
    outline: "none" as const,
    transition: "border-color 0.2s ease, box-shadow 0.2s ease, background 0.2s ease",
    fontFamily: "'DM Sans', sans-serif",
    boxSizing: "border-box" as const,
    boxShadow: focusedField === field ? "0 0 0 3px rgba(40, 71, 158, 0.12)" : "none",
  });

  return (
    <>
      <style>{`

        .book-a-free-call-page {
          --ppp-blue: ${COL.blue};
          --ppp-blue-dark: ${COL.blueDark};
          --ppp-orange: ${COL.orange};
          --ppp-text: ${COL.text};
          --ppp-text-muted: ${COL.textMuted};
          --ppp-text-soft: ${COL.textSoft};
          --ppp-border: ${COL.border};
          --ppp-surface: ${COL.surface};
          --ppp-surface-subtle: ${COL.surfaceSubtle};

          width: 100%;
          min-height: 120vh;
          position: relative;
          overflow-x: clip;
          font-family: 'DM Sans', system-ui, sans-serif;
          color: var(--ppp-text);
          background: var(--ppp-surface);
        }

        .book-a-free-call-page,
        .book-a-free-call-page *,
        .book-a-free-call-page *::before,
        .book-a-free-call-page *::after {
          box-sizing: border-box;
        }

        .bfc-hero-shell {
          position: relative;
          width: 100%;
          min-height: var(--hero-section-min-height);
          display: flex;
          align-items: center;
          justify-content: center;
          overflow: hidden;
          isolation: isolate;
          background: #0f172a;
        }

        .bfc-hero-bg-img {
          position: absolute;
          inset: 0;
          width: 100%;
          height: 100%;
          object-fit: cover;
          object-position: center;
          z-index: 0;
          transform: scale(1.02);
        }

        .bfc-hero-overlay {
          position: absolute;
          inset: 0;
          z-index: 1;
          pointer-events: none;
          background: linear-gradient(
            180deg,
            rgba(15, 23, 42, 0.5) 0%,
            rgba(15, 23, 42, 0.72) 45%,
            rgba(15, 23, 42, 0.88) 100%
          );
        }

        .bfc-hero-content {
          position: relative;
          z-index: 2;
          width: 100%;
          max-width: var(--hero-section-max-width);
          margin: 0 auto;
          padding: var(--hero-section-block-padding) var(--hero-section-inline-padding);
          display: flex;
          justify-content: var(--hero-section-justify);
          text-align: var(--hero-section-text-align);
        }

        .book-a-free-call-inner {
          position: relative;
          z-index: 1;
          width: 100%;
          max-width: min(1320px, 100%);
          margin: 0 auto;
          padding: clamp(16px, 3vw, 40px) clamp(16px, 4vw, 48px) clamp(64px, 8vw, 100px);
        }

        .book-a-free-call-inner::before {
          content: '';
          position: absolute;
          inset: 0;
          z-index: 0;
          pointer-events: none;
          border-radius: 0;
          background-image:
            linear-gradient(rgba(40, 71, 158, 0.04) 1px, transparent 1px),
            linear-gradient(90deg, rgba(40, 71, 158, 0.04) 1px, transparent 1px);
          background-size: 40px 40px;
          background-position: -1px -1px;
        }

        .book-a-free-call-inner .main-grid {
          position: relative;
          z-index: 1;
        }

        .hero {
          text-align: center;
          padding: 0;
          max-width: 56rem;
          width: 100%;
          margin-left: auto;
          margin-right: auto;
        }

        .hero h1,
        .hero .hero-sub {
          text-align: center;
        }

        .hero--on-image h1 {
          color: #ffffff;
          text-shadow: 0 1px 2px rgba(0, 0, 0, 0.75), 0 2px 16px rgba(0, 0, 0, 0.4);
        }

        .hero--on-image h1 .accent {
          color: #fde68a;
        }

        .hero--on-image h1 .accent::after {
          background: linear-gradient(90deg, #f58030 0%, rgba(245, 128, 48, 0.2) 100%);
        }

        .hero--on-image .hero-sub {
          color: rgba(248, 250, 252, 0.95);
          text-shadow: 0 1px 2px rgba(0, 0, 0, 0.8), 0 1px 10px rgba(0, 0, 0, 0.45);
        }

        .hero--on-image .hero-badge {
          background: rgba(15, 23, 42, 0.55);
          border: 1px solid rgba(255, 255, 255, 0.28);
          color: #ffffff;
        }

        .hero--on-image .hero-badge span {
          background: #f58030;
        }

        .hero-badge {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          background: rgba(40, 71, 158, 0.08);
          border: 1px solid rgba(40, 71, 158, 0.22);
          border-radius: 100px;
          padding: 8px 18px;
          font-size: 13px;
          font-weight: 600;
          text-align: center;
          justify-content: center;
          color: var(--ppp-blue);
          letter-spacing: 0.03em;
          margin-bottom: 24px;
        }

        .hero-badge span {
          display: inline-block;
          width: 7px;
          height: 7px;
          background: var(--ppp-orange);
          border-radius: 50%;
          animation: bfc-pulse-dot 1.6s ease-in-out infinite;
        }

        @keyframes bfc-pulse-dot {
          0%, 100% { opacity: 1; transform: scale(1); }
          50% { opacity: 0.55; transform: scale(0.85); }
        }

        .hero h1 {
          font-family: 'Syne', sans-serif;
          font-size: clamp(2rem, 5vw, 3.5rem);
          font-weight: 800;
          line-height: 1.1;
          letter-spacing: -0.02em;
          color: var(--ppp-text);
          margin-bottom: 16px;
        }

        .hero h1 .accent {
          color: var(--ppp-blue);
          position: relative;
        }

        .hero h1 .accent::after {
          content: '';
          position: absolute;
          bottom: 2px;
          left: 0;
          width: 100%;
          height: 3px;
          border-radius: 2px;
          background: linear-gradient(90deg, var(--ppp-blue) 0%, rgba(40, 71, 158, 0.15) 100%);
        }

        .hero-sub {
          font-size: clamp(1rem, 2vw, 1.125rem);
          color: var(--ppp-text-muted);
          font-weight: 400;
          max-width: 36rem;
          margin: 0;
          line-height: 1.65;
        }

        .main-grid {
          display: grid;
          grid-template-columns: minmax(0, 1fr) minmax(0, 2.05fr);
          gap: clamp(20px, 3vw, 32px);
          align-items: start;
          width: 100%;
        }

        @media (max-width: 960px) {
          .main-grid {
            grid-template-columns: 1fr;
          }
          .benefits-col { order: 2; }
          .bfc-booking-area { order: 1; }
        }

        .bfc-booking-area {
          display: flex;
          flex-direction: column;
          gap: clamp(18px, 3vw, 26px);
          min-width: 0;
        }

        .bfc-consult-panel {
          background: var(--ppp-surface);
          border: 1px solid var(--ppp-border);
          border-radius: 20px;
          padding: clamp(22px, 3vw, 34px) clamp(22px, 3vw, 36px);
          box-shadow: 0 4px 20px rgba(15, 23, 42, 0.05);
          position: relative;
          overflow: hidden;
        }

        .bfc-consult-panel::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          height: 3px;
          background: linear-gradient(90deg, var(--ppp-blue), var(--ppp-orange));
          opacity: 0.85;
        }

        .bfc-consult-kicker {
          font-size: 12px;
          font-weight: 700;
          letter-spacing: 0.08em;
          text-transform: uppercase;
          color: var(--ppp-blue);
          margin-bottom: 10px;
        }

        .bfc-consult-heading {
          font-family: 'Syne', sans-serif;
          font-size: clamp(1.35rem, 2.8vw, 1.75rem);
          font-weight: 800;
          color: var(--ppp-text);
          line-height: 1.2;
          margin-bottom: 10px;
          letter-spacing: -0.02em;
        }

        .bfc-consult-lead {
          font-size: 15px;
          color: var(--ppp-text-muted);
          line-height: 1.65;
          margin-bottom: 22px;
          max-width: 52rem;
        }

        .bfc-consult-list {
          list-style: none;
          margin: 0;
          padding: 0;
          display: flex;
          flex-direction: column;
          gap: 16px;
        }

        .bfc-consult-item {
          display: flex;
          gap: 14px;
          align-items: flex-start;
        }

        .bfc-consult-icon {
          flex-shrink: 0;
          width: 36px;
          height: 36px;
          border-radius: 10px;
          background: rgba(40, 71, 158, 0.1);
          border: 1px solid rgba(40, 71, 158, 0.2);
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 16px;
          color: var(--ppp-blue);
        }

        .bfc-consult-item-title {
          font-size: 15px;
          font-weight: 700;
          color: var(--ppp-text);
          margin-bottom: 4px;
          line-height: 1.35;
        }

        .bfc-consult-item-body {
          font-size: 14px;
          color: var(--ppp-text-soft);
          line-height: 1.6;
        }

        .bfc-consult-foot {
          margin-top: 20px;
          padding-top: 16px;
          border-top: 1px solid var(--ppp-border);
          font-size: 13px;
          color: var(--ppp-text-soft);
          line-height: 1.55;
        }

        .benefit-card {
          background: var(--ppp-surface);
          border: 1px solid var(--ppp-border);
          border-radius: 16px;
          padding: 22px 20px;
          margin-bottom: 14px;
          box-shadow: 0 1px 2px rgba(15, 23, 42, 0.04);
          transition: transform 0.2s ease, border-color 0.2s ease, box-shadow 0.2s ease;
          position: relative;
          overflow: hidden;
        }

        .benefit-card::before {
          content: '';
          position: absolute;
          left: 0;
          top: 0;
          bottom: 0;
          width: 3px;
          background: var(--ppp-blue);
          border-radius: 2px 0 0 2px;
          opacity: 0;
          transition: opacity 0.2s ease;
        }

        .benefit-card:hover {
          transform: translateY(-3px);
          border-color: rgba(40, 71, 158, 0.25);
          box-shadow: 0 12px 28px rgba(40, 71, 158, 0.08);
        }

        .benefit-card:hover::before { opacity: 1; }

        .benefit-icon { font-size: 20px; margin-bottom: 8px; }
        .benefit-title { font-size: 16px; font-weight: 700; color: var(--ppp-text); margin-bottom: 6px; }
        .benefit-desc { font-size: 14px; color: var(--ppp-text-soft); line-height: 1.55; }

        .bfc-inline-link {
          color: var(--ppp-blue);
          text-decoration: underline;
          text-underline-offset: 3px;
          font-weight: 600;
        }

        .bfc-inline-link:hover {
          color: var(--ppp-blue-dark);
        }

        .bfc-inline-link--on-dark {
          color: #fbbf24;
          text-decoration: underline;
          text-underline-offset: 3px;
          font-weight: 600;
        }

        .bfc-inline-link--on-dark:hover {
          color: #fde68a;
        }

        .form-card {
          background: var(--ppp-surface);
          border: 1px solid var(--ppp-border);
          border-radius: 20px;
          padding: clamp(28px, 4vw, 40px) clamp(22px, 3vw, 36px);
          box-shadow: 0 4px 24px rgba(15, 23, 42, 0.06);
          position: relative;
          overflow: hidden;
        }

        .form-card::after {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          height: 3px;
          background: linear-gradient(90deg, transparent, var(--ppp-blue), var(--ppp-orange), transparent);
          opacity: 0.9;
        }

        .form-title {
          font-family: 'sans-serif';
          font-size: clamp(1.25rem, 2.5vw, 1.5rem);
          font-weight: 800;
          color: var(--ppp-text);
          margin-bottom: 6px;
          text-align: center;
        }

        .form-subtitle {
          font-size: 14px;
          color: var(--ppp-text-soft);
          text-align: center;
          margin-bottom: 22px;
          line-height: 1.5;
        }

        .bfc-schedule-wrap {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: clamp(16px, 3vw, 24px);
          align-items: start;
        }

        @media (max-width: 640px) {
          .bfc-schedule-wrap {
            grid-template-columns: 1fr;
          }
        }

        .bfc-panel-title {
          font-size: 12px;
          font-weight: 700;
          letter-spacing: 0.06em;
          text-transform: uppercase;
          color: var(--ppp-text-muted);
          margin-bottom: 10px;
          padding-bottom: 10px;
          border-bottom: 1px solid var(--ppp-border);
        }

        .bfc-cal-nav {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 8px;
          margin-bottom: 12px;
        }

        .bfc-cal-nav button {
          width: 36px;
          height: 36px;
          border-radius: 10px;
          border: 1px solid var(--ppp-border);
          background: var(--ppp-surface-subtle);
          color: var(--ppp-text);
          cursor: pointer;
          font-size: 18px;
          line-height: 1;
          transition: background 0.2s, border-color 0.2s;
        }

        .bfc-cal-nav button:hover {
          border-color: rgba(40, 71, 158, 0.35);
          background: rgba(40, 71, 158, 0.06);
        }

        .bfc-cal-nav button:focus-visible {
          outline: 2px solid var(--ppp-orange);
          outline-offset: 2px;
        }

        .bfc-cal-month {
          font-family: 'Arial', sans-serif;
          font-weight: 800;
          font-size: 1rem;
          color: var(--ppp-text);
        }

        .bfc-weekdays {
          display: grid;
          grid-template-columns: repeat(7, 1fr);
          gap: 4px;
          margin-bottom: 6px;
        }

        .bfc-weekdays span {
          font-size: 11px;
          font-weight: 600;
          color: var(--ppp-text-soft);
          text-align: center;
        }

        .bfc-days {
          display: grid;
          grid-template-columns: repeat(7, 1fr);
          gap: 4px;
        }

        .bfc-day-cell {
          aspect-ratio: 1;
          min-height: 36px;
          border-radius: 10px;
          border: 1px solid transparent;
          background: transparent;
          font-size: 13px;
          font-weight: 600;
          color: var(--ppp-text);
          cursor: pointer;
          transition: background 0.15s, color 0.15s, border-color 0.15s;
        }

        .bfc-day-cell:hover:not(:disabled) {
          background: rgba(40, 71, 158, 0.08);
        }

        .bfc-day-cell:disabled {
          opacity: 0.28;
          cursor: not-allowed;
        }

        .bfc-day-cell--muted {
          visibility: hidden;
          pointer-events: none;
        }

        .bfc-day-cell--selected {
          background: var(--ppp-blue);
          color: #fff;
          border-color: var(--ppp-blue-dark);
        }

        .bfc-day-cell--today:not(.bfc-day-cell--selected) {
          border-color: rgba(40, 71, 158, 0.35);
        }

        .bfc-avail-head {
          font-size: 15px;
          font-weight: 700;
          color: var(--ppp-text);
          margin-bottom: 6px;
          line-height: 1.35;
        }

        .bfc-avail-sub {
          font-size: 13px;
          color: var(--ppp-text-soft);
          margin-bottom: 14px;
        }

        .bfc-slot-grid {
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          gap: 8px;
        }

        @media (min-width: 520px) {
          .bfc-slot-grid {
            grid-template-columns: repeat(3, 1fr);
          }
        }

        .bfc-slot {
          padding: 10px 8px;
          border-radius: 10px;
          border: 1.5px solid var(--ppp-border);
          background: var(--ppp-surface-subtle);
          font-size: 13px;
          font-weight: 600;
          color: var(--ppp-text);
          cursor: pointer;
          transition: border-color 0.2s, background 0.2s, box-shadow 0.2s;
        }

        .bfc-slot:hover {
          border-color: rgba(40, 71, 158, 0.4);
          background: rgba(40, 71, 158, 0.06);
        }

        .bfc-slot--on {
          border-color: var(--ppp-blue);
          background: rgba(40, 71, 158, 0.1);
          box-shadow: 0 0 0 2px rgba(40, 71, 158, 0.12);
        }

        .bfc-slot:focus-visible {
          outline: 2px solid var(--ppp-orange);
          outline-offset: 2px;
        }

        .bfc-actions-row {
          display: flex;
          flex-wrap: wrap;
          gap: 10px;
          margin-top: 18px;
          align-items: center;
          justify-content: flex-end;
        }

        .bfc-link-back {
          margin-right: auto;
          background: none;
          border: none;
          color: var(--ppp-blue);
          font-weight: 600;
          font-size: 14px;
          cursor: pointer;
          text-decoration: underline;
          text-underline-offset: 3px;
          font-family: inherit;
        }

        .bfc-link-back:hover {
          color: var(--ppp-blue-dark);
        }

        .cta-btn {
          min-width: 140px;
          padding: 14px 22px;
          background: var(--ppp-blue);
          color: #ffffff;
          font-family: 'Syne', sans-serif;
          font-size: 15px;
          font-weight: 700;
          border: none;
          border-radius: 12px;
          cursor: pointer;
          letter-spacing: 0.02em;
          transition: transform 0.2s ease, background 0.2s ease, box-shadow 0.2s ease;
          box-shadow: 0 4px 14px rgba(40, 71, 158, 0.35);
        }

        .cta-btn:hover:not(:disabled) {
          background: var(--ppp-blue-dark);
          transform: translateY(-1px);
          box-shadow: 0 8px 22px rgba(40, 71, 158, 0.4);
        }

        .cta-btn:disabled {
          opacity: 0.45;
          cursor: not-allowed;
          transform: none;
          box-shadow: none;
        }

        .cta-btn:focus-visible {
          outline: 2px solid var(--ppp-orange);
          outline-offset: 3px;
        }

        .form-group { margin-bottom: 16px; }

        .form-label {
          display: block;
          font-size: 12px;
          font-weight: 600;
          color: var(--ppp-text-muted);
          letter-spacing: 0.05em;
          text-transform: uppercase;
          margin-bottom: 8px;
        }

        .form-footnote {
          text-align: center;
          margin-top: 14px;
          font-size: 13px;
          color: var(--ppp-text-soft);
        }

        .bfc-error {
          font-size: 14px;
          color: #b91c1c;
          margin-bottom: 12px;
          line-height: 1.45;
        }

        .bfc-summary {
          background: var(--ppp-surface-subtle);
          border: 1px solid var(--ppp-border);
          border-radius: 12px;
          padding: 12px 14px;
          margin-bottom: 18px;
          font-size: 14px;
          color: var(--ppp-text-muted);
          line-height: 1.5;
        }

        .bfc-summary strong {
          color: var(--ppp-text);
        }

        .bfc-check-grid {
          display: flex;
          flex-direction: column;
          gap: 10px;
        }

        .bfc-check {
          display: flex;
          align-items: center;
          gap: 10px;
          font-size: 15px;
          color: var(--ppp-text);
          cursor: pointer;
          user-select: none;
        }

        .bfc-check input {
          width: 18px;
          height: 18px;
          accent-color: var(--ppp-blue);
        }

        .trust-strip {
          display: flex;
          gap: 12px;
          margin-top: 16px;
          padding-top: 16px;
          border-top: 1px solid var(--ppp-border);
        }

        .trust-item {
          flex: 1;
          text-align: center;
          padding: 12px 8px;
          background: var(--ppp-surface-subtle);
          border-radius: 12px;
          border: 1px solid var(--ppp-border);
        }

        .trust-num {
          font-family: 'Syne', sans-serif;
          font-size: 1.25rem;
          font-weight: 800;
          color: var(--ppp-blue);
        }

        .trust-label {
          font-size: 11px;
          font-weight: 600;
          color: var(--ppp-text-soft);
          margin-top: 4px;
          letter-spacing: 0.04em;
          text-transform: uppercase;
        }

        @media (prefers-reduced-motion: reduce) {
          .hero-badge span { animation: none; }
        }
      `}</style>

      <div className="book-a-free-call-page">
        <section className="bfc-hero-shell hero-section" aria-label="Book a free consultation call">
          <PictureImage
            className="bfc-hero-bg-img"
            src={HERO_BG_SRC}
            alt="Consultation call for Amazon FBA and eCommerce fulfilment services UK"
            width={1920}
            height={1280}
            decoding="async"
            isHero
          />
          <div className="bfc-hero-overlay" aria-hidden />
          <div className="bfc-hero-content">
            <motion.div
              ref={heroRef}
              className="  hero--on-image"
              initial={{ opacity: 0, y: 24 }}
              animate={heroInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.55, ease: "easeOut" }}
            >
              <motion.div
                className="hero-badge"
                initial={{ opacity: 0, scale: 0.96 }}
                animate={heroInView ? { opacity: 1, scale: 1 } : {}}
                transition={{ delay: 0.08, duration: 0.45 }}
              >
                <span /> Free & No-Obligation Consultation
              </motion.div>

              <motion.h1
                initial={{ opacity: 0, y: 20 }}
                animate={heroInView ? { opacity: 1, y: 0 } : {}}
                transition={{ delay: 0.12, duration: 0.55 }}
              >
                Book A <span style={{ color: '#f97316' }}>Free</span> Consultation Call
              </motion.h1>

              <motion.p
                className="hero-sub"
                initial={{ opacity: 0, y: 12 }}
                animate={heroInView ? { opacity: 1, y: 0 } : {}}
                transition={{ delay: 0.22, duration: 0.5 }}
              >
                Speak with our{" "}
                <Link to="/amazon-fba-prep-wholesale" className="bfc-inline-link bfc-inline-link--on-dark">wholesale FBA prep experts</Link> and receive tailored guidance on{" "}
                <Link to="/amazon-fba-prep-services" className="bfc-inline-link bfc-inline-link--on-dark">Amazon FBA prep services UK</Link> with no pressure or commitment. New to inbound rules? Read{" "}
                <Link to="/blog/what-is-fba-prep" className="bfc-inline-link bfc-inline-link--on-dark">what is FBA prep</Link> before your call.
              </motion.p>
            </motion.div>
          </div>
        </section>

        <div className="book-a-free-call-inner">
          <div className="main-grid">
            <div className="benefits-col" ref={benefitsRef}><h2 style={{ fontSize: "1.75rem", color: "#1e293b", marginBottom: "1.5rem", fontWeight: "700" }}>Why Book a Call?</h2>
              {benefits.map((b, i) => (
                <motion.div
                  key={b.title}
                  className="benefit-card"
                  initial={{ opacity: 0, x: -28 }}
                  animate={benefitsInView ? { opacity: 1, x: 0 } : {}}
                  transition={{ delay: 0.1 * i, duration: 0.5, ease: "easeOut" }}
                >
                  <div className="benefit-icon">{b.icon}</div>
                  <div className="benefit-title">{b.title}</div>
                  <div className="benefit-desc">{b.desc}</div>
                </motion.div>
              ))}

             
            </div>

            <div className="bfc-booking-area" ref={formRef}>
             

              <motion.div
                className="form-card"
                initial={{ opacity: 0, y: 28 }}
                animate={formInView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.55, ease: "easeOut", delay: 0.06 }}
              >
                <AnimatePresence mode="wait">
                  {bookingStep === "schedule" ? (
                    <motion.div
                      key="schedule"
                      initial={{ opacity: 0, y: 8 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -8 }}
                      transition={{ duration: 0.35 }}
                    >
                      <div className="form-title">Schedule your call</div>
                      <div className="form-subtitle">
                        Choose a date and select a UK time slot between 09:00 and 12:00. We&apos;ll confirm your consultation by email — you can also review{" "}
                        <Link to="/amazon-fba-prep-pricing" className="bfc-inline-link">FBA prep pricing UK</Link> or{" "}
                        <Link to="/pricing-fulfilment-services" className="bfc-inline-link">fulfilment pricing</Link> ahead of the call.
                      </div>

                      <div className="bfc-schedule-wrap">
                        <div>
                          <div className="bfc-panel-title">Select a date</div>
                          <div className="bfc-cal-nav">
                            <button type="button" onClick={goPrevMonth} aria-label="Previous month">
                              ‹
                            </button>
                            <span className="bfc-cal-month">{monthLabel}</span>
                            <button type="button" onClick={goNextMonth} aria-label="Next month">
                              ›
                            </button>
                          </div>
                          <div className="bfc-weekdays">
                            {WEEKDAY_LABELS.map((w) => (
                              <span key={w}>{w}</span>
                            ))}
                          </div>
                          <div className="bfc-days">
                            {calendarCells.map((cell, idx) => {
                              if (!cell) {
                                return <span key={`e-${idx}`} className="bfc-day-cell bfc-day-cell--muted" />;
                              }
                              const { date } = cell;
                              const past = isBeforeTodayLocal(date);
                              const selected = selectedDay && sameLocalDay(date, selectedDay);
                              const today = sameLocalDay(date, new Date());
                              return (
                                <button
                                  key={cell.day}
                                  type="button"
                                  className={[
                                    "bfc-day-cell",
                                    selected ? "bfc-day-cell--selected" : "",
                                    today ? "bfc-day-cell--today" : "",
                                  ]
                                    .filter(Boolean)
                                    .join(" ")}
                                  disabled={past}
                                  onClick={() => {
                                    setSelectedDay(date);
                                    setSelectedSlot(null);
                                  }}
                                >
                                  {cell.day}
                                </button>
                              );
                            })}
                          </div>
                        </div>

                        <div>
                          <div className="bfc-panel-title">Check availability (UK)</div>
                          <div className="bfc-avail-head">
                            {selectedDay ? `Times for ${formatLongDate(selectedDay)}` : "Choose a date to see times"}
                          </div>
                          <p className="bfc-avail-sub">
                            All slots are UK local time (Europe/London). Planning for{" "}
                            <Link to="/blog/amazon-ending-fba-prep-services-uk-2026" className="bfc-inline-link">July 2026 Amazon prep changes</Link>? Mention it on the call.
                          </p>
                          <div className="bfc-slot-grid" role="group" aria-label="Available call times">
                            {UK_TIME_SLOTS.map((slot) => {
                              const on = selectedSlot === slot;
                              return (
                                <button
                                  key={slot}
                                  type="button"
                                  className={`bfc-slot${on ? " bfc-slot--on" : ""}`}
                                  disabled={!selectedDay}
                                  onClick={() => setSelectedSlot(slot)}
                                >
                                  {slot}
                                </button>
                              );
                            })}
                          </div>
                          <div className="bfc-actions-row">
                            <motion.button
                              type="button"
                              className="cta-btn"
                              disabled={!canProceedToDetails}
                              onClick={() => setBookingStep("details")}
                              whileTap={{ scale: 0.98 }}
                            >
                              Next
                            </motion.button>
                          </div>
                        </div>
                      </div>

                      <p className="form-footnote">
                        Your details are safe with us. No spam, ever. Prefer email?{" "}
                        <Link to="/contact-fulfillment-services" className="bfc-inline-link">Contact our team</Link> directly.
                      </p>
                    </motion.div>
                  ) : (
                    <motion.div
                      key="details"
                      initial={{ opacity: 0, y: 8 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -8 }}
                      transition={{ duration: 0.35 }}
                    >
                      <div className="form-title">Your details</div>
                      <div className="form-subtitle">
                        Tell us a bit about your business — whether you need{" "}
                        <Link to="/amazon-fba-prep-services" className="bfc-inline-link">FBA prep</Link>,{" "}
                        <Link to="/multi-channel-fulfillment-services" className="bfc-inline-link">multi-channel fulfilment</Link>, or{" "}
                        <Link to="/amazon-fba-shipments" className="bfc-inline-link">Amazon FBA shipments</Link>. We&apos;ll include this with your booking.
                      </div>

                      {selectedDay && selectedSlot && (
                        <div className="bfc-summary">
                          <strong>Preferred call:</strong> {formatLongDate(selectedDay)} at <strong>{selectedSlot}</strong>{" "}
                          UK
                        </div>
                      )}

                      {submitError ? <div className="bfc-error">{submitError}</div> : null}

                      <form onSubmit={handleBookingSubmit} noValidate>
                        <div className="form-group">
                          <label className="form-label" htmlFor="bfc-d-name">
                            Full name *
                          </label>
                          <input
                            id="bfc-d-name"
                            name="name"
                            type="text"
                            required
                            autoComplete="name"
                            value={detailForm.name}
                            onChange={handleDetailChange}
                            onFocus={() => setFocusedField("d-name")}
                            onBlur={() => setFocusedField(null)}
                            style={inputStyle("d-name")}
                            placeholder="e.g. James Harper"
                          />
                        </div>

                        <div className="form-group">
                          <label className="form-label" htmlFor="bfc-d-email">
                            Email address *
                          </label>
                          <input
                            id="bfc-d-email"
                            name="email"
                            type="email"
                            required
                            autoComplete="email"
                            value={detailForm.email}
                            onChange={handleDetailChange}
                            onFocus={() => setFocusedField("d-email")}
                            onBlur={() => setFocusedField(null)}
                            style={inputStyle("d-email")}
                            placeholder="you@example.com"
                          />
                        </div>

                        <div className="form-group">
                          <label className="form-label" htmlFor="bfc-d-phone">
                            Phone number
                          </label>
                          <input
                            id="bfc-d-phone"
                            name="phone"
                            type="tel"
                            autoComplete="tel"
                            value={detailForm.phone}
                            onChange={handleDetailChange}
                            onFocus={() => setFocusedField("d-phone")}
                            onBlur={() => setFocusedField(null)}
                            style={inputStyle("d-phone")}
                            placeholder="+44 7700 000000"
                          />
                        </div>

                        <div className="form-group">
                          <label className="form-label" htmlFor="bfc-d-company">
                            Company trading name *
                          </label>
                          <input
                            id="bfc-d-company"
                            name="companyTradingName"
                            type="text"
                            required
                            value={detailForm.companyTradingName}
                            onChange={handleDetailChange}
                            onFocus={() => setFocusedField("d-company")}
                            onBlur={() => setFocusedField(null)}
                            style={inputStyle("d-company")}
                            placeholder="Registered or trading name"
                          />
                        </div>

                        <div className="form-group">
                          <label className="form-label" htmlFor="bfc-d-years">
                            Number of years trading *
                          </label>
                          <input
                            id="bfc-d-years"
                            name="yearsTrading"
                            type="text"
                            inputMode="decimal"
                            required
                            value={detailForm.yearsTrading}
                            onChange={handleDetailChange}
                            onFocus={() => setFocusedField("d-years")}
                            onBlur={() => setFocusedField(null)}
                            style={inputStyle("d-years")}
                            placeholder="e.g. 3"
                          />
                        </div>

                        <div className="form-group">
                          <span className="form-label">Service requirements *</span>
                          <div className="bfc-check-grid">
                            <label className="bfc-check">
                              <input
                                type="checkbox"
                                name="fulfilment"
                                checked={detailForm.fulfilment}
                                onChange={handleDetailChange}
                              />
                              Fulfilment
                            </label>
                            <label className="bfc-check">
                              <input type="checkbox" name="fbaPrep" checked={detailForm.fbaPrep} onChange={handleDetailChange} />
                              FBA Prep
                            </label>
                            <label className="bfc-check">
                              <input
                                type="checkbox"
                                name="logistics"
                                checked={detailForm.logistics}
                                onChange={handleDetailChange}
                              />
                              Logistics
                            </label>
                          </div>
                        </div>

                        <div className="bfc-actions-row" style={{ marginTop: 8 }}>
                          <button
                            type="button"
                            className="bfc-link-back"
                            onClick={() => {
                              setSubmitError("");
                              setBookingStep("schedule");
                            }}
                          >
                            ← Change date or time
                          </button>
                          <motion.button
                            type="submit"
                            className="cta-btn"
                            disabled={isSubmitting}
                            whileTap={{ scale: 0.98 }}
                          >
                            {isSubmitting ? "Sending…" : "Submit booking"}
                          </motion.button>
                        </div>
                      </form>

                      <p className="form-footnote">
                        Your details are safe with us. No spam, ever. Prefer email?{" "}
                        <Link to="/contact-fulfillment-services" className="bfc-inline-link">Contact our team</Link> directly.
                      </p>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
